import buildElement from "./buildElement";
import generateGuid from "@/lib/generateGuid";
import { HiddenValueMode } from "@cs/HiddenValueMode";
import distinct from "@/lib/distinct";
import { BehaviorSubject, ReplaySubject, combineLatest, from } from "rxjs";
import { map, switchMap, first, /*publishBehavior, refCount, finalize,*/ share /*tap*/ } from "rxjs/operators";
import { isNullOrWhitespace, stringCoalesce } from "@/lib/whitespaceMethods";
import { FormItem } from "@cs/FormItem";

import { IElementSettings } from "@cs-ts/i-element-settings";
import { IControlSettings } from "@cs-ts/i-control-settings";
import { FormComment } from "@cs-ts/form-comment";
import { FormHistory } from "@cs-ts/form-history";
import { FormIssue } from "@/cs-ts-bridge/ts/form-issue";
import { FormFileDependency } from "@cs/FormFileDependency";
import { CalculationContext } from "./__CalculationContextInterface";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { Observable } from "rxjs";
import { IHiddenValueModeSettings } from "@cs-ts/i-hidden-value-mode-settings";
import { PageSettings } from "@/cs-ts-bridge/ts/page-settings";
import { SubmitTriggerSettings } from "@/cs-ts-bridge/ts/submit-trigger-settings";
import { FormControlItem } from "@/cs-ts-bridge/cs/FormControlItem";
import { FormBaseControlItem } from "@/cs-ts-bridge/cs/FormBaseControlItem_1";
import type FormDef from "./FormDef";
import type { resolveCalculation } from "../../lib/calculations.direct";
import { InvokeBody } from "@cs-ts/invoke-body";
import { useMainStore } from "@/mainStore";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";

// TODO move to a separate file?
export type Awaitable<T> = T | PromiseLike<T>;
export type ChangeType = "changed" | "deleted" | "new" | null;

// TODO - proper types

let _resolveCalculation: Promise<{ resolveCalculation: typeof resolveCalculation }> | null = null;

// TODO - proper types
export interface ElementData {
    [key: string]: null | string | string[] | null | boolean | ElementData;
}

export type BaseElementDefSimple<TSetting extends IElementSettings> = BaseElementDef<TSetting, FormDefinitionItem<TSetting>, FormItem>;
export type BaseElementDefBase = BaseElementDef<IElementSettings, FormDefinitionItem<IElementSettings>, FormItem>;
//eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Change = { key: string | null; baseSetting: any; currentSetting: any };

export class BaseElementDef<TSetting extends IElementSettings, TDef extends FormDefinitionItem<TSetting>, TFormItem extends FormItem> {
    declare definition: TDef;
    declare parentElement: BaseElementDefBase | null;
    declare children: BaseElementDefBase[];
    declare isBase: boolean | undefined;
    declare baseLink: BaseElementDefBase | undefined;
    declare isFocusable: boolean | undefined;
    declare forceNonLive: boolean | undefined;
    declare forceMaskedOutput: boolean | undefined;
    declare typeName: string;
    declare id: string | undefined;
    declare vmId: string;
    declare comments: FormComment[];
    declare history: FormHistory[];
    declare issues: FormIssue[];
    declare calculationContext: CalculationContext;
    declare fixedCalculationResults: Record<string, string[]>;
    declare dependencies: Record<string, string | string[] | FormFileDependency>;
    declare dependenciesIndex: number[];
    declare foreachCounts: Record<string, number[]>;

    declare stub: boolean | null;
    declare fixedCalculationResults$: BehaviorSubject<Record<string, string[]>>;
    declare _children$: BehaviorSubject<BaseElementDefBase[]>;
    declare _edited$: BehaviorSubject<boolean>;
    //declare changes: Change[];
    declare baseChanges: Change[];
    //@ts-ignore-error
    declare _baseElementValid$: { [k: boolean]: { [k: boolean]: Observable<boolean> | undefined } } | null;
    declare customErrorIds: string[];
    declare _customErrorIds$: BehaviorSubject<string[]>;

    constructor(parentElement: BaseElementDefBase | null, elementData: TFormItem, definitionData: TDef, calculationContext: CalculationContext) {
        this.parentElement = parentElement;
        this.id = definitionData.id;
        this.typeName = definitionData.fullType;
        this.definition = definitionData;
        /* @type {string[]} */
        this.comments = elementData == null ? [] : elementData.comments || [];
        this.history = elementData == null ? [] : elementData.history || [];
        this.issues = elementData == null ? [] : elementData.issues || [];
        /** @type {import ("./__CalculationContextInterface").CalculationContext} */
        this.calculationContext = calculationContext;
        if (parentElement == null && calculationContext.rootElement == null) {
            calculationContext.rootElement = this;
        }
        this.dependenciesIndex = [];
        this.dependencies = {};
        this.foreachCounts = {};
        const typeNameMatch = /[.]([^.]*)Def,/.exec(this.definition.fullType);
        if (typeNameMatch == null) {
            throw new Error(JSON.stringify(this.definition));
        }
        this.typeName = "MI.Common.DynamicForms.Models.DF" + typeNameMatch[1]; // TODO - not hardcoded?
        this.stub = null;
        /** @type {Record<string,string[]>} */
        this.fixedCalculationResults = {};
        /** @type {BehaviorSubject<Record<string,string[]>>} */
        this.fixedCalculationResults$ = new BehaviorSubject({} as Record<string, string[]>);
        this.setData(elementData, true);
        this.children = [];
        this._children$ = new BehaviorSubject([] as BaseElementDefBase[]);
        this._edited$ = new BehaviorSubject(false);
        this.initChildren(elementData);
        //this.changes = [];
        this.baseLink = undefined;
        this.isBase = undefined;
        this.isFocusable = undefined;
        this.customErrorIds = [];
        this._customErrorIds$ = new BehaviorSubject(this.customErrorIds);
    }
    getSegment(): string | undefined {
        return this.parentElement?.getSegment();
    }

    setKey(data: TFormItem | null, name: string, saveKey?: string | null, value?: string | boolean | null) {
        saveKey = saveKey || name;
        //console.log(`A - setKey$ for ${this.definition.id} - A - ${saveKey} - ${JSON.stringify(value)}`);
        if (value == null && data != null) {
            //@ts-ignore TODO?
            value = data[name];
        }
        //@ts-ignore TODO?
        this[saveKey] = value;
        //@ts-ignore TODO?
        if (this[saveKey + "$"] == null) {
            //@ts-ignore TODO?
            this[saveKey + "$"] = new BehaviorSubject(this[saveKey]);
            //@ts-ignore TODO?
        } else if (value != this[saveKey + "$"].getValue()) {
            //@ts-ignore TODO?
            this[saveKey + "$"].next(this[saveKey]);
        }
        //console.log(`Z - setKey$ for ${this.definition.id} - Z - ${saveKey} - ${JSON.stringify(value)}`);
    }

    initChildren(data: TFormItem) {
        //@ts-ignore TODO
        this.children = (this.definition.children || []).map((childDefinition, index) => {
            const childData = data ? (data.children && data.children.length > index ? data.children[index] : ({} as FormItem)) : null;
            return buildElement(this, childData!, childDefinition, this.calculationContext);
        });
        this._children$.next(this.children);
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolveCalculation(id: string, legacyCalc?: any, debug?: boolean): Observable<string[]> {
        if (id == null && legacyCalc == null) {
            return from([[]]);
        }
        if (_resolveCalculation == null) {
            _resolveCalculation = import(/* webpackChunkName: "CalculationDirect", webpackPrefetch: true */ "../../lib/calculations.direct");
        }
        const calculation = this.definition.settings.calculations[id] || legacyCalc;
        if (calculation == null) {
            return from([[]]);
        }
        const func = _resolveCalculation!.then(module => module.resolveCalculation(calculation, this, debug)) as Promise<() => Observable<string[]>>;
        return combineLatest([from(func), this.fixedCalculationResults$]).pipe(
            switchMap(([result, fixedCalculationResults]) => {
                if (fixedCalculationResults != null && id in fixedCalculationResults) {
                    return from([fixedCalculationResults[id]]);
                } else {
                    return result();
                }
            }),
        );
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    async invokeRawAsync(type: string, data: string, adjuster?: (a: InvokeBody) => InvokeBody, abortController?: AbortController | null) {
        const formNode = this.getRoot() as FormDef;
        const url = await this.calculationContext.urlFetcher("invokeDraft", { formId: formNode.formId! });
        const mainStore = useMainStore();
        const writeSegment = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.WriteSegmentInInvoke.Id);
        let postData: InvokeBody = {
            formName: formNode.formName,
            formVersion: formNode.formVersion,
            formHash: formNode.formHash,
            segment: writeSegment ? this.getSegment() : null!,
            skipForm: true,
            noWrite: true,
            asAdmin: this.calculationContext.adminReview || false, // TODO
            inPreview: this.calculationContext.adminPreview || false, // TODO
            testMode: this.calculationContext.testMode || false, // TODO
            target: this.id,
            targetPathArray: this.getElementPathIndexFromRoot(),
            type: type,
            data: data,
            setUser: false,
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
        };
        if (adjuster != null) {
            postData = adjuster(postData);
        }
        const resp = await this.calculationContext.fetchXsrf(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(postData),
            signal: abortController?.signal,
        });
        return await resp.json();
    }

    async invokeAsync<TResult>(type: string, data: string, adjuster?: (a: InvokeBody) => InvokeBody, abortController?: AbortController | null) {
        const outerResult = await this.invokeRawAsync(type, data, adjuster, abortController);
        const result = JSON.parse(outerResult.Result) as TResult;
        return result;
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolveCalculationPromiseBool(id: string, legacyCalc?: any, debug?: boolean) {
        const result = this.resolveCalculation(id, legacyCalc, debug);
        return combineLatest([result, _resolveCalculation])
            .pipe(
                //@ts-ignore
                map(([value, module]) => {
                    //@ts-ignore
                    return value.reduce((a, i) => a || module.parseBool(i) === 1, false);
                }),
                first(),
            )
            .toPromise();
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolveCalculationPromise(id: string, legacyCalc?: any, debug?: boolean) {
        return this.resolveCalculation(id, legacyCalc, debug).pipe(first()).toPromise();
    }

    getVirtualContext$(): Observable<string[]> {
        return this.parentElement != null ? this.parentElement.getVirtualContext$() : from([[]]);
    }

    getCalculation(id: string, fallback: unknown) {
        throw new Error();
    }

    effectiveExtraData$(type: string, fallbackCalculation: (extraCacheKey$?: Observable<string>) => Observable<string[] | null>) {
        return fallbackCalculation();
    }

    visible$(): Observable<boolean> {
        if (this.parentElement != null) {
            return this.parentElement.visible$();
        } else {
            return from([true]);
        }
    }

    effectiveHiddenValueMode(): HiddenValueMode {
        if (((this.definition.settings as IHiddenValueModeSettings).hiddenValueMode ?? HiddenValueMode.Inherit) == HiddenValueMode.Inherit) {
            if (this.parentElement != null) {
                return this.parentElement.effectiveHiddenValueMode();
            } else {
                return HiddenValueMode.Blank;
            }
        } else {
            return (this.definition.settings as IHiddenValueModeSettings).hiddenValueMode!;
        }
    }

    setData(data: FormItem | null, excludeChildren?: boolean) {
        this.vmId = this.vmId || generateGuid();
        this.stub = data == null;
        if (data != null) {
            this.comments = data.comments;
            this.history = data.history;
            this.issues = data.issues ?? [];
            this.fixedCalculationResults = data.fixedCalculationResults;
            this.fixedCalculationResults$.next(this.fixedCalculationResults);
        }
        if (!excludeChildren) {
            this.children.forEach((child, index) => {
                if (data == null || (data.children && data.children.length > index)) {
                    child.setData(data == null ? null : data.children[index]);
                }
            });
        }
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    getData(excludeChildren?: boolean): TFormItem | null {
        if (this.stub) {
            return null;
        }
        const valid$ = this.valid$();
        const item = {} as TFormItem;
        item.children = excludeChildren ? null! : this.children.map(c => c.getData(false)!);
        item.id = this.id;
        //@ts-expect-error
        item.typeName = this.typeName;
        item.valid = valid$ != null && (valid$ as BehaviorSubject<boolean>).getValue != null ? (valid$ as BehaviorSubject<boolean>).getValue() : true;
        return item;
    }

    getCustomPageNav(): BaseElementDefBase[] {
        return this.children.filter(i => i != null).reduce((a, c) => a.concat(c.getCustomPageNav()), [] as BaseElementDefBase[]);
    }

    hasCustomPageNav() {
        return this.getCustomPageNav().length > 0;
    }

    getLabel() {
        //@ts-expect-error
        return this.definition.settings.shortLabel || this.definition.settings.label || this.definition.settings.displayLabel || this.definition.settings.adminDisplayLabel;
    }

    getElementByVmId(vmId: string): BaseElementDefBase | null {
        if (this.vmId == vmId) {
            return this;
        }
        let result = null;
        this.children
            .filter(i => i != null)
            .some(v => {
                result = v.getElementByVmId(vmId);
                return result != null;
            });
        return result;
    }

    getElementPathByVmId(vmId: string) {
        if (this.vmId == vmId) {
            return [this];
        }
        let result = null;
        this.children
            .filter(i => i != null)
            .some(v => {
                result = v.getElementPathByVmId(vmId);
                return result != null;
            });
        if (result == null) {
            return null;
        }
        return [this].concat(result);
    }

    getElementPathIndexFromRoot() {
        const result = [];
        let el = this as BaseElementDefBase; // eslint-disable-line @typescript-eslint/no-this-alias
        while (el.parentElement != null) {
            result.push(el.parentElement.children.indexOf(el));
            el = el.parentElement;
        }
        result.reverse();
        return result;
    }

    getElementsByFilter2<T extends BaseElementDefBase>(func: (el: BaseElementDefBase) => el is T): T[] {
        return this.getElementsByFilter(func) as T[];
    }

    getElementsByFilter(func: (el: BaseElementDefBase) => boolean) {
        const result = [] as BaseElementDefBase[];
        this._getElementsByFilter(func, result);
        return result;
    }

    _getElementsByFilter(func: (el: BaseElementDefBase) => boolean, result: BaseElementDefBase[]) {
        if (func.apply(this, [this])) {
            result.push(this);
        }
        this.children.filter(i => i != null).forEach(child => child._getElementsByFilter(func, result));
    }

    /** @returns {Observable<BaseElementDef[]>} */
    getElementsById$(id: string, contextVmId?: string) {
        const result = this._getElementsById$(id, contextVmId);
        if (result.length == 0) {
            return from([[]]);
        }
        //@ts-expect-error TODO?
        const observable = result.filter(i => i.pipe) as Observable<BaseElementDefBase[]>[];
        //@ts-expect-error TODO?
        const nonObservable = result.filter(i => !i.pipe) as BaseElementDefBase[];
        if (nonObservable.length > 0) {
            observable.push(from([nonObservable]));
        }
        return observable.length == 0 ? from([[]]) : observable.length == 1 ? observable[0] : combineLatest(observable).pipe(map(i => flattenArrays(i)));
    }

    getSegmentView(segment: string | null, triggerKey: string | null): BaseElementDefBase {
        if (segment == null) {
            return this;
        }
        return new SegmentView(this, segment, triggerKey) as unknown as BaseElementDefBase;
    }

    getValuesById$(id: string, contextVmId: string) {
        return this.getElementsById$(id, contextVmId).pipe(switchMap(elements => combineLatest(elements.map(i => (i as unknown as BaseControlDefBase).effectiveValueList$())).pipe(map(arraysOfValues => flattenArrays(arraysOfValues)))));
    }

    _getElementsById$(id: string, contextVmId?: string): (Observable<BaseElementDefBase[]> | BaseElementDefBase)[] {
        if (this.id == id) {
            return [this];
        }
        if (this.children.filter(i => i != null).length == 0) {
            return [];
        }
        return flattenArrays(this.children.filter(i => i != null).map(child => child._getElementsById$(id, contextVmId)));
    }

    valid$(includeUnedited?: boolean, excludeChildren?: boolean): Observable<boolean> {
        //const oid = generateGuid();
        //console.log(`${oid} - Starting valid$ for ${this.definition.id}, ${includeUnedited}, ${excludeChildren}`);
        includeUnedited = !!includeUnedited;
        excludeChildren = !!excludeChildren;
        if (this._baseElementValid$ == null) {
            this._baseElementValid$ = {
                true: { true: undefined, false: undefined },
                false: { true: undefined, false: undefined },
            };
        }
        //@ts-expect-error
        if (this._baseElementValid$[includeUnedited][excludeChildren] == null) {
            if (excludeChildren) {
                //@ts-expect-error
                this._baseElementValid$[includeUnedited][excludeChildren] = from([true]);
            } else {
                const result = this._children$.pipe(
                    switchMap(children =>
                        children.filter(i => i != null).length == 0
                            ? from([[]])
                            : combineLatest(
                                  children
                                      .filter(i => i != null)
                                      .map(c => {
                                          return combineLatest(c.valid$(includeUnedited, excludeChildren), this._validateChild$(c)).pipe(
                                              //tap(i => { debugger; }),
                                              map(([valid, validateChild]) => (validateChild ? valid : true)),
                                          );
                                      }),
                              ),
                    ),
                    map(results => results.every(i => i)),
                );
                const finalResult = result.pipe(share({ resetOnRefCountZero: true, connector: () => new ReplaySubject(1) }));
                //@ts-expect-error
                this._baseElementValid$[includeUnedited][excludeChildren] = finalResult;
            }
        }
        //@ts-expect-error
        return this._baseElementValid$[includeUnedited][excludeChildren]; //.pipe(finalize(() => console.log(`${oid} - Finishing valid$ for ${this.definition.id}, ${includeUnedited}, ${excludeChildren}`)));
    }

    errors$() {
        return from([[] as string[]]);
    }

    customErrorIds$() {
        return this._customErrorIds$.pipe(map(i => (i.length > 0 ? i.join(" ") : "")));
    }

    async firstInvalidElement(includeUnedited?: boolean): Promise<BaseElementDefBase | null> {
        const [thisValid, thisHasCustomErrors] = await Promise.all([this.valid$(includeUnedited, true).pipe(first()).toPromise(), this.customErrorIds$().pipe(first()).toPromise()]);
        if ((!thisValid || !isNullOrWhitespace(thisHasCustomErrors)) && this.isFocusable) {
            return this;
        }
        for (const child of this.children) {
            const childFirst = await child.firstInvalidElement();
            if (childFirst != null) {
                return childFirst;
            }
        }
        return null;
    }

    firstFocusableElement(): BaseElementDefBase | null {
        if (this.isFocusable) {
            return this;
        }
        for (const child of this.children) {
            const childFirst = child.firstFocusableElement();
            if (childFirst != null) {
                return childFirst;
            }
        }
        return null;
    }

    /**
     * Gets errors of child elements
     */
    async descendentsErrors(): Promise<string[]> {
        const totalErrors = [];
        const thisErrors = await this.errors$().pipe(first()).toPromise();
        if (thisErrors != null && thisErrors.length > 0) {
            totalErrors.push(thisErrors);
        }
        for (const child of this.children) {
            const errors = await child.descendentsErrors();
            if (errors != null && errors.length > 0) {
                totalErrors.push(errors);
            }
        }
        return flattenArrays(totalErrors);
    }

    edited$() {
        return this._edited$;
    }

    markEdited() {
        this._edited$.next(true);
    }

    /** @returns {Observable<boolean>} */
    _validateChild$(child: BaseElementDefBase): Observable<boolean> {
        return this.parentElement != null ? this.parentElement._validateChild$(this) : from([true]);
    }

    /** @returns {Observable<boolean>} */
    _validateUneditedChild$(child: BaseElementDefBase): Observable<boolean> {
        return this.parentElement != null ? this.parentElement._validateUneditedChild$(this) : from([true]);
    }

    /** @returns {Observable<boolean>} */
    _validateUnedited$(): Observable<boolean> {
        return this.parentElement != null ? this.parentElement._validateUneditedChild$(this) : from([true]);
    }

    _maskedValue$(): Observable<string[]> {
        return this.parentElement != null ? this.parentElement._maskedValue$() : from([[]]);
    }

    _effectiveLabelText$(): Observable<string | null | undefined> {
        return this.parentElement != null ? this.parentElement._effectiveLabelText$() : from([]);
    }

    /** @returns {BaseElementDef} */
    getRoot(): BaseElementDefBase {
        return this.parentElement != null ? this.parentElement.getRoot() : this;
    }

    markElementAsBase() {
        this.isBase = true;
        this.children
            .filter(i => i != null)
            .forEach(child => {
                child.markElementAsBase();
            });
    }

    linkBaseElement(baseElement: BaseElementDefBase | null) {
        // TODO does foreach need special handling?
        if (baseElement != null && ((this.definition.type == "FormDef" && baseElement.definition.type == "FormDef") || (this.definition.type == "FormTemplateDef" && baseElement.definition.type == "FormTemplateDef") || (this.id != null && this.id == baseElement.id))) {
            baseElement.vmId = this.vmId;
            baseElement.baseLink = this;
            this.baseLink = baseElement;
            this.baseChanges = this.diffBaseElement();
        }
        this.isBase = false;
        this.children
            .filter(i => i != null)
            .forEach(child => {
                const matchedElement = baseElement == null ? [] : baseElement.children.filter(i => i != null).filter(i => (child.definition.type == "FormTemplateDef" && i.definition.type == "FormTemplateDef") || (i.id != null && i.id == child.id));
                child.linkBaseElement(matchedElement.length > 0 ? matchedElement[0] : null);
            });
        if (baseElement != null) {
            baseElement.isBase = true;
            baseElement.children
                .filter(i => i != null)
                .filter(i => !this.children.filter(i => i != null).some(j => j.id == i.id))
                .forEach(child => {
                    child.markElementAsBase();
                });
        }
    }

    baseChangeType(): ChangeType {
        if (this.baseLink == null) {
            return this.isBase ? "deleted" : "new";
        }
        if (this.baseChanges != null && this.baseChanges.length > 0) {
            return "changed";
        }
        return null;
    }

    baseChangeRecursive(): ChangeType {
        let changeType = this.baseChangeType();
        if (changeType == null) {
            if (this.children.length != this.baseLink!.children.length || this.children.some(child => child.baseChangeRecursive() != null) || this.baseLink!.children.some(child => child.baseChangeType() != null)) {
                changeType = "changed";
            }
        }
        return changeType;
    }

    diffBaseElement() {
        if (this.baseLink == null) {
            return [];
        }
        const result = [] as Change[];
        const baseSettings = this.baseLink.definition.settings;
        const currentSettings = this.definition.settings;
        function compare(prefix: string | null, baseSettings: unknown, currentSettings: unknown) {
            function compareKey(prefixedKey: string | null, baseSetting: unknown, currentSetting: unknown) {
                if (Array.isArray(baseSetting == null ? currentSetting : baseSetting)) {
                    const baseSettingA: unknown[] = (baseSetting as unknown[]) || [];
                    const currentSettingA: unknown[] = (currentSetting as unknown[]) || [];
                    if (baseSettingA.length !== currentSettingA.length) {
                        result.push({ key: prefixedKey + ".length", baseSetting: baseSettingA.length, currentSetting: currentSettingA.length });
                    }
                    for (let index = 0; index < Math.max(baseSettingA.length, currentSettingA.length); index++) {
                        compare(prefixedKey + "[" + index + "]", baseSettingA[index], currentSettingA[index]);
                    }
                } else if ((baseSetting == null ? typeof currentSetting : typeof baseSetting) == "object") {
                    compare(prefixedKey, baseSetting, currentSetting);
                } else {
                    if (JSON.stringify(baseSetting) !== JSON.stringify(currentSetting)) {
                        result.push({ key: prefixedKey, baseSetting, currentSetting });
                    }
                }
            }
            if ((baseSettings == null ? typeof currentSettings : typeof baseSettings) == "object") {
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
                const baseSettingsA: Record<any, unknown> = (baseSettings as Record<any, unknown>) || {};
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
                const currentSettingsA: Record<any, unknown> = (currentSettings as Record<any, unknown>) || {};
                distinct(Object.keys(baseSettingsA).concat(Object.keys(currentSettingsA))).forEach(key => {
                    const prefixedKey = prefix == null ? key : key != null ? prefix + "." + key : prefix;
                    const baseSetting = baseSettings ? baseSettingsA[key] : null;
                    const currentSetting = currentSettings ? currentSettingsA[key] : null;
                    compareKey(prefixedKey, baseSetting, currentSetting);
                });
            } else {
                compareKey(prefix, baseSettings, currentSettings);
            }
        }
        compare(null, baseSettings, currentSettings);
        return result;
    }
}

function splitValidationList(text: string) {
    if (text == null || text.length == 0) {
        return [];
    }
    return text.match(/(\\.|[^\\\\`])+/g)!.map(function (v) {
        return v.replace(/\\`/g, "`").replace(/\\\\/g, "\\");
    });
}

function toUpperCaseList(list: string[]) {
    return list.map(function (v) {
        return toUpperCase(v);
    });
}

function toUpperCase(v: string) {
    return v && v.toUpperCase ? v.toUpperCase().trim() : v;
}

function buildFullMatcher<T>(matcher: (a: T, b: T) => boolean, all: boolean, match: T, matchList: T[]) {
    return function (v: T) {
        if (match) {
            if (matcher(v, match)) {
                if (!all) {
                    return true;
                }
            } else if (all) {
                return false;
            }
        }
        let result = all;
        matchList.some(function (v2) {
            result = matcher(v, v2);
            return all ? !result : result;
        });
        return result;
    };
}
function dataMatch(matcher: (a: string, b: string) => boolean, all: boolean) {
    return function (dataList: string[], match: string, matchList: string[]) {
        if (dataList.length == 0) {
            return all;
        }
        const convert = function (v: string) {
            return v == null ? "" : v;
        };
        const matcher3 = function (i: string, j: string) {
            return matcher(convert(i), convert(j));
        };
        const matcher2 = buildFullMatcher(matcher3, all, match, matchList);
        let result = all;
        dataList.some(function (v) {
            result = matcher2(v);
            return all ? !result : result;
        });
        return result;
    };
}
function numberMatch(matcher: (a: number, b: number) => boolean) {
    return function (dataList: string[], match: string, matchList: string[]) {
        const matcher2 = buildFullMatcher(matcher, true, parseFloat(match), matchList.map(parseFloat));
        /** @type {string | boolean} */
        let result: boolean | "dependency-mismatch" = true;
        dataList.forEach(function (v) {
            const dataNumber = parseFloat(v);
            if (isNaN(dataNumber)) {
                result = "dependency-mismatch";
                return false;
            }
            result = result && matcher2(dataNumber);
            return result;
        });
        return result;
    };
}

function valueMatch(dataList: string[], match: string | null, matchList: string[], matchType: string) {
    const matcher =
        matchType == "Equals"
            ? dataMatch(function (i, j) {
                  return i == j;
              }, false)
            : matchType == "NotEquals"
              ? dataMatch(function (i, j) {
                    return i != j;
                }, true)
              : matchType == "LessThan"
                ? numberMatch(function (i, j) {
                      return i < j;
                  })
                : matchType == "LessThanOrEqualTo"
                  ? numberMatch(function (i, j) {
                        return i <= j;
                    })
                  : matchType == "GreaterThan"
                    ? numberMatch(function (i, j) {
                          return i > j;
                      })
                    : matchType == "GreaterThanOrEqualTo"
                      ? numberMatch(function (i, j) {
                            return i >= j;
                        })
                      : null;
    return matcher!(dataList, match!, matchList);
}

function isValueSet(values: string[]) {
    return values != null && values.some(value => value != null && !/^\s*$/.test(value));
}

function formatIfOtherMessage(rules: Record<string, string>, otherValue: string[], ruleKey: string) {
    const message = rules[ruleKey];
    const matches = toUpperCaseList(splitValidationList(rules[`${ruleKey}-matches`]));
    const matchLabels = splitValidationList(rules[`${ruleKey}-matchlabels`]);
    const otherMatch = otherValue.filter(v => matches.some(m => m == toUpperCase(v), matches));
    const index = matches.indexOf(toUpperCase(otherMatch[0]));
    const formattedMessage = message.replace("{0}", matchLabels[index]);
    return formattedMessage;
}

function flattenArrays<T>(arrays: T[][]): T[] {
    return Array.prototype.concat.apply([], arrays);
}

//@ts-expect-error
export class SegmentView extends BaseElementDef<IElementSettings, FormDefinitionItem<IElementSettings>, SegmentView> {
    declare wrapped: BaseElementDefBase;
    declare segment: string | null;
    declare triggerKey: string | null;
    constructor(wrapped: BaseElementDefBase, segment: string | null, triggerKey: string | null) {
        super(wrapped.parentElement, { comments: wrapped.comments, history: wrapped.history, issues: wrapped.issues, segment, triggerKey, children: wrapped.children as unknown as FormItem[] } as unknown as SegmentView, wrapped.definition, wrapped.calculationContext);
        this.wrapped = wrapped;
        this.segment = segment;
        this.triggerKey = triggerKey;
    }

    initChildren(data: SegmentView) {
        this.children = (data.children as unknown as BaseElementDefBase[])
            .filter(i => i != null)
            .filter(i => (i.definition.type == "PageDef" || i.definition.type == "CaptchaControlDef" ? (i.definition.settings as PageSettings).segment == data.segment : i.definition.type == "StateFormDef" ? i.id == data.segment : i.definition.type == "SubmitTriggerDef" ? data.triggerKey == null || (i.definition.settings as SubmitTriggerSettings).triggerKey == data.triggerKey : i.definition.type == "FormReminderDef" ? false : i.definition.type == "FormExpireDef" ? false : true))
            .map(i => new SegmentView(i, (data as unknown as SegmentView).segment, (data as unknown as SegmentView).triggerKey)) as unknown as BaseElementDefBase[];
        this._children$.next(this.children);
    }

    _getElementsByFilter(func: (el: BaseElementDefBase) => boolean, result: BaseElementDefBase[]) {
        if (this.wrapped.definition.type == "WorkflowSwitchDef") {
            this.wrapped._getElementsByFilter(func, result);
            return;
        }
        if (func.apply(this.wrapped, [this.wrapped])) {
            result.push(this.wrapped);
        }
        this.children.filter(i => i != null).forEach(child => child._getElementsByFilter(func, result));
    }

    getElementByVmId(vmId: string) {
        if (this.wrapped.definition.type == "WorkflowSwitchDef") {
            return this.wrapped.getElementByVmId(vmId);
        }
        if (this.wrapped.vmId == vmId) {
            return this.wrapped;
        }
        let result = null;
        this.children
            .filter(i => i != null)
            .some(v => {
                result = v.getElementByVmId(vmId);
                return result != null;
            });
        return result;
    }
    _getElementsById$(id: string, contextVmId?: string): (Observable<BaseElementDefBase[]> | BaseElementDefBase)[] {
        if (this.wrapped.definition.type == "WorkflowSwitchDef") {
            return this.wrapped._getElementsById$(id, contextVmId);
        }
        if (this.wrapped.id == id) {
            return [this.wrapped];
        }
        if (this.children.filter(i => i != null).length == 0) {
            return [];
        }
        return flattenArrays(this.children.filter(i => i != null).map(child => child._getElementsById$(id, contextVmId)));
    }
}

export type BaseControlDefBase = BaseControlDef<IControlSettings, FormDefinitionItem<IControlSettings>, FormControlItem, string | string[]>;

export class BaseControlDef<TSetting extends IControlSettings, TDef extends FormDefinitionItem<TSetting>, TFormItem extends FormBaseControlItem<TValue>, TValue> extends BaseElementDef<TSetting, TDef, TFormItem> {
    declare _value: TValue;
    declare _value$: BehaviorSubject<TValue>;
    declare _effectiveValueList$: Observable<string[]>;
    declare _hasValue: boolean;
    declare _hasValue$: BehaviorSubject<boolean>;
    declare autoEdit: boolean;
    declare _baseControlValid$: { [k in "true" | "false"]: { [k in "true" | "false"]: undefined | Observable<boolean> } };
    declare _baseControlErrors$: Observable<string[]> | null;
    declare _effectiveLabel$: Observable<string | null> | null;
    declare clientValidateRules: Record<string, string>;
    declare clientValidateRules$: BehaviorSubject<Record<string, string>>;

    constructor(parentElement: BaseElementDefBase | null, elementData: TFormItem, definitionData: TDef, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this._value = (elementData == null ? this.getValueFromDependencies() : elementData.value) as TValue;
        this._value$ = this._value$ || new BehaviorSubject(this._value);
        //console.log(`${definitionData.settings.label} - ${elementData?.hasValue}`);
        this._hasValue = elementData == null || Object.keys(elementData).length == 0 ? this.getValueFromDependencies() != null : elementData.hasValue;
        this._hasValue$ = this._hasValue$ || new BehaviorSubject(this._hasValue);
        //this._effectiveValue$ = this._value.pipe(this.getEffectivePipe());
        this.clientValidateRules$ = this.clientValidateRules$ || new BehaviorSubject(this.clientValidateRules || {});
        this.autoEdit = true;
    }
    setData(data: TFormItem | null, excludeChildren?: boolean) {
        super.setData(data, excludeChildren);
        this.setKey(data, "value", "_value");
        this.setKey(data, "hasValue", "_hasValue");
        this.setKey(data, "clientValidateRules");
    }
    getValueFromDependencies() {
        const indexList = this.calculationContext.rootElement?.dependenciesIndex;
        const index = indexList == null || indexList.length == 0 ? 0 : indexList[0];
        const dependencies = (this.calculationContext.rootElement?.dependencies ?? {}) as Record<string, TValue[]>;
        if (this.id! in dependencies) {
            return dependencies[this.id!][index];
        } else {
            return null;
        }
    }
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    setKey(data: TFormItem | null, name: string, saveKey?: string | null, value?: any | null) {
        if (value == null && data == null && name == "value") {
            value = this.getValueFromDependencies();
        }
        if (value == null && data == null && name == "hasValue") {
            value = this.getValueFromDependencies() != null;
        }
        super.setKey(data, name, saveKey, value);
    }
    getData(excludeChildren?: boolean) {
        const result = super.getData(excludeChildren);
        if (result != null) {
            //@ts-expect-error
            result.value = this._value == null ? "" : this._value;
            //@ts-expect-error
            result.valueSet = this._hasValue;
        }
        return result;
    }
    valid$(includeUnedited?: boolean, excludeChildren?: boolean): Observable<boolean> {
        includeUnedited = !!includeUnedited;
        excludeChildren = !!excludeChildren;
        //const oid = generateGuid();
        //console.log(`${oid} - Starting C valid$ for ${this.definition.id}`);
        if (this._baseControlValid$ == null) {
            this._baseControlValid$ = {
                true: { true: undefined, false: undefined },
                false: { true: undefined, false: undefined },
            };
        }
        if (this._baseControlValid$[includeUnedited.toString() as "true" | "false"][excludeChildren.toString() as "true" | "false"] == null) {
            const valid$ = super.valid$(includeUnedited, excludeChildren);
            const hasErrors$ = this.errors$().pipe(map(i => i.length == 0));
            const result = combineLatest(valid$, this.edited$(), this._validateUnedited$(), hasErrors$).pipe(map(([superValid, edited, validateUnedited, thisValid]) => superValid && (edited || includeUnedited || validateUnedited ? thisValid : true)));
            this._baseControlValid$[includeUnedited.toString() as "true" | "false"][excludeChildren.toString() as "true" | "false"] = result.pipe(share({ resetOnRefCountZero: true, connector: () => new ReplaySubject(1) }));
        }
        return this._baseControlValid$[includeUnedited.toString() as "true" | "false"][excludeChildren.toString() as "true" | "false"]!; //.pipe(finalize(() => console.log(`${oid} - Finishing C valid$ for ${this.definition.id}`)));
    }
    effectiveLabel$() {
        if (this._effectiveLabel$ == null) {
            if ((this.definition.settings as unknown as { labelCalculationId: string }).labelCalculationId) {
                this._effectiveLabel$ = this.resolveCalculation((this.definition.settings as unknown as { labelCalculationId: string }).labelCalculationId).pipe(
                    map(i => (i.length ? i[0] : null)),
                    share({ resetOnRefCountZero: true, connector: () => new BehaviorSubject(null as null | string) }),
                );
            } else {
                this._effectiveLabel$ = from([this.definition.settings.label ?? null]);
            }
        }
        return this._effectiveLabel$!;
    }
    /** @returns {Observable<string>} */
    effectiveShortLabel$(): Observable<string | null> {
        if (this.definition.settings.shortLabel) {
            return from([this.definition.settings.shortLabel]);
        } else {
            return this.effectiveLabel$();
        }
    }
    /** @returns {Observable<string>} */
    effectiveLabelText$(): Observable<string | null | undefined> {
        return combineLatest(this.effectiveShortLabel$(), this.effectiveLabel$()).pipe(map(([s, l]) => stringCoalesce(l, s, null)));
    }
    setValue(value: TValue) {
        //console.log(`A - setValue$ for ${this.definition.id} - Z - ${JSON.stringify(value)}`);
        const edited = this.autoEdit && this._value != value;
        this._value = value;
        if (this._value$.getValue() != value) {
            this._value$.next(value);
        }
        this._hasValue = true;
        if (!this._hasValue$.getValue()) {
            this._hasValue$.next(true);
        }
        if (edited) {
            this.markEdited();
        }
        //console.log(`Z - setValue$ for ${this.definition.id} - Z - ${JSON.stringify(value)}`);
    }
    value() {
        return this._value;
    }
    value$() {
        return this._value$;
    }
    /** @returns {Observable<string[]>} */
    valueList$() {
        return this._value$.pipe(map(value => (value == null ? [] : Array.isArray(value) ? value : [value])));
    }
    effectiveValue$() {
        throw new Error("NYI");
    }
    hasValue$() {
        return this._hasValue$;
    }
    /** @returns {Observable<string[]>} */
    defaultValue$() {
        if (this.definition.settings.defaultValueCalculationId) {
            return this.resolveCalculation(this.definition.settings.defaultValueCalculationId).pipe(map(i => i.filter(i => !isNullOrWhitespace(i))));
        } else {
            const defaultV = this.definition.settings.defaultValue;
            if (isNullOrWhitespace(defaultV)) {
                return from([[]]);
            } else {
                return from([[defaultV]]);
            }
        }
    }

    /** @returns {Observable<string[]>} */
    effectiveValueList$() {
        //const oid = generateGuid();
        //console.log(`${oid} - Starting effectiveValueList$ for ${this.definition.id}`);
        if (this._effectiveValueList$ == null) {
            const pipes = [
                //tap(v => {
                //    console.log(`0 - Redoing effectiveValueList$ for ${this.definition.id} - Z - ${JSON.stringify(v)}`);
                //}),
                map((value: string[]) => (value == null ? [] : Array.isArray(value) ? value : [value]).filter(i => !isNullOrWhitespace(i))),
                //tap(v => {
                //    console.log(`1 - Redoing effectiveValueList$ for ${this.definition.id} - Z - ${JSON.stringify(v)}`);
                //}),
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
            ] as any[];
            pipes.push(
                switchMap(value => {
                    //console.log(`A - Redoing effectiveValueList$ for ${this.definition.id} - A - ${JSON.stringify(value)}`);
                    const effectiveHiddenValueMode = this.effectiveHiddenValueMode();
                    const getValue = () =>
                        this._hasValue$.pipe(
                            switchMap(hasValue => {
                                //console.log(`C - Redoing effectiveValueList$ for ${this.definition.id} - C - ${hasValue}`);
                                if (hasValue) {
                                    return from([value]);
                                } else {
                                    return this.defaultValue$();
                                }
                            }),
                        );
                    if (effectiveHiddenValueMode == HiddenValueMode.EnteredValue) {
                        return getValue();
                    } else {
                        return this.visible$().pipe(
                            switchMap(v => {
                                //console.log(`B - Redoing effectiveValueList$ for ${this.definition.id} - B - ${v}`);
                                if (v) {
                                    return getValue();
                                } else if (effectiveHiddenValueMode == HiddenValueMode.Blank) {
                                    return from([[]]);
                                } else if (effectiveHiddenValueMode == HiddenValueMode.DefaultValue) {
                                    return this.defaultValue$();
                                } else {
                                    debugger;
                                    return from([[]]);
                                }
                            }),
                        );
                    }
                }),
            );
            //pipes.push(tap(v => {
            //    console.log(`3 - Redoing effectiveValueList$ for ${this.definition.id} - Z - ${JSON.stringify(v)}`);
            //}));
            const source$ = this._value$;
            //@ts-ignore TODO?
            const sourceTransformed$ = source$.pipe.apply(source$, pipes) as Observable<string[]>; //eslint-disable-line prefer-spread
            this._effectiveValueList$ = sourceTransformed$.pipe(
                //tap(v => {
                //    console.log(`4 - Redoing effectiveValueList$ for ${this.definition.id} - Z - ${JSON.stringify(v)}`);
                //}),
                share({ resetOnRefCountZero: true, connector: () => new ReplaySubject(1) }),
                //tap(v => {
                //    console.log(`5 - Redoing effectiveValueList$ for ${this.definition.id} - Z - ${JSON.stringify(v)}`);
                //}),
            );
        }
        //@ts-ignore
        return this._effectiveValueList$; //.pipe(finalize(() => console.log(`${oid} - Finishing effectiveValueList$ for ${this.definition.id}`)));
    }
    effectiveValueList(): Promise<string[]> {
        return this.effectiveValueList$()
            .pipe(first(null, [] as string[]))
            .toPromise() as Promise<string[]>;
    }
    rules$() {
        return this.clientValidateRules$.pipe(map(rules => Object.assign({} as Record<string, string>, rules == null ? this.definition.clientValidateRules : rules)));
    }
    errors$(): Observable<string[]> {
        //const oid = generateGuid();
        //console.log(`${oid} - Starting errors$ for ${this.definition.id}`);
        if (this._baseControlErrors$ == null) {
            const result = this.rules$().pipe(
                switchMap(rules => {
                    if (Object.keys(rules).length == 0) {
                        return from([[] as string[]]);
                    }
                    const effectiveValueList$ = this.effectiveValueList$();
                    const result$s: Observable<string[]>[] = [];
                    const ruleKeys = Object.keys(rules);
                    function simpleValidation(func: (a: string[]) => string[] | undefined) {
                        result$s.push(
                            effectiveValueList$.pipe(
                                map(func),
                                map(i => (i == undefined ? [] : i)),
                            ),
                        );
                    }
                    function ifOtherMatchesValidation(this: BaseControlDef<TSetting, TDef, TFormItem, TValue>, rules: Record<string, string>, ruleKey: string, func: (valueList: string[], overValue: string[]) => string[] | undefined) {
                        const otherElementResult$ = ifOtherMatchesSubject.apply(this, [rules, ruleKey]);
                        result$s.push(
                            combineLatest([otherElementResult$, effectiveValueList$]).pipe(
                                map(([otherElementResult, valueList]) => {
                                    const [otherValue, isMatch] = otherElementResult;
                                    if (isMatch) {
                                        const result = func(valueList, otherValue);
                                        return result == undefined ? [] : result;
                                    }
                                    return [];
                                }),
                            ),
                        );
                    }

                    function ifOtherMatchesSubject(this: BaseControlDef<TSetting, TDef, TFormItem, TValue>, rules: Record<string, string>, ruleKey: string) {
                        const matchValues = splitValidationList(rules[`${ruleKey}-matches`]);
                        const otherElementResult$ = this.calculationContext.rootElement!.getValuesById$(rules[`${ruleKey}-other`], this.vmId).pipe(map(otherValue => [otherValue, valueMatch(otherValue, null, matchValues.length == 0 ? [""] : matchValues, "Equals")] as const));
                        return otherElementResult$;
                    }

                    for (let index = 0; index < ruleKeys.length; index++) {
                        const ruleKey = ruleKeys[index];
                        const message = rules[ruleKey];
                        switch (ruleKey) {
                            // cspell:ignore requiredifotherhasvalue, requiredfileifotherhasvalue, requireexactcountifotherhasvalue, requireoneifotherhasvalue, requireoneifotherhasvalue, requiredvalueifotherhasvalue, requiredvalueifotherhasvalue, requiredvalueifotherhasvalue, requiredvalueifotherhasvalue
                            case "data-val-serveronlyerror": {
                                simpleValidation(values => {
                                    const matchValues = splitValidationList(rules["data-val-serveronlyerror-values"]);
                                    if (valueMatch(values, null, matchValues.length == 0 ? [""] : matchValues, "Equals") || (matchValues.length == 0 && values.length == 0)) {
                                        return [message];
                                    }
                                });
                                break;
                            }
                            case "data-val-required": {
                                simpleValidation(values => {
                                    if (!isValueSet(values)) {
                                        return [message];
                                    }
                                });
                                break;
                            }
                            case "data-val-requiredfile": {
                                result$s.push(this.hasValue$().pipe(map(hasValue => (!hasValue ? [message] : []))));
                                break;
                            }
                            case "data-val-requiredifotherhasvalue": {
                                ifOtherMatchesValidation.apply(this, [
                                    rules,
                                    ruleKey,
                                    (valueList: string[], otherValue: string[]) => {
                                        if (!isValueSet(valueList)) {
                                            const formattedMessage = formatIfOtherMessage(rules, otherValue, ruleKey);
                                            return [formattedMessage];
                                        }
                                    },
                                ]);
                                break;
                            }
                            case "data-val-requiredfileifotherhasvalue": {
                                const otherElementResult$ = ifOtherMatchesSubject.apply(this, [rules, ruleKey]);
                                result$s.push(
                                    combineLatest([otherElementResult$, this.hasValue$()]).pipe(
                                        map(([otherElementResult, hasValue]) => {
                                            const [otherValue, isMatch] = otherElementResult;
                                            if (isMatch && !hasValue) {
                                                const formattedMessage = formatIfOtherMessage(rules, otherValue, ruleKey);
                                                return [formattedMessage];
                                            }
                                            return [];
                                        }),
                                    ),
                                );
                                break;
                            }
                            case "data-val-requireexactcountifotherhasvalue": {
                                const counts = splitValidationList(rules[`${ruleKey}-counts`]);
                                const others = splitValidationList(rules[`${ruleKey}-others`]);
                                const matchValues = splitValidationList(rules[`${ruleKey}-matches`]);
                                const otherElementResult$ = combineLatest(others.map(other => this.calculationContext.rootElement!.getValuesById$(other, this.vmId))).pipe(
                                    map(otherValues => {
                                        for (let matchIndex = 0; matchIndex < matchValues.length; matchIndex++) {
                                            for (let dataIndex = 0; dataIndex < otherValues.length; dataIndex++) {
                                                if (valueMatch(otherValues[dataIndex], null, [matchValues[matchIndex]], "Equals")) {
                                                    return [true, matchIndex];
                                                }
                                            }
                                        }
                                        return [false, null];
                                    }),
                                );
                                result$s.push(
                                    combineLatest([effectiveValueList$, otherElementResult$]).pipe(
                                        map(([values, otherElementResult]) => {
                                            const [isMatch, matchIndex] = otherElementResult;
                                            //@ts-expect-error
                                            const count = parseInt(counts[matchIndex]);
                                            if (isMatch && values != null && values.length != count) {
                                                const message = rules[ruleKey];
                                                const matchLabels = splitValidationList(rules[`${ruleKey}-matchlabels`]);
                                                const otherLabels = splitValidationList(rules[`${ruleKey}-otherlabels`]);
                                                //@ts-expect-error
                                                const formattedMessage = message.replace("{0}", counts[matchIndex]).replace("{1}", otherLabels[matchIndex]).replace("{2}", matchLabels[matchIndex]);
                                                return [formattedMessage];
                                            }
                                            return [];
                                        }),
                                    ),
                                );
                                break;
                            }
                            case "data-val-requireoneifotherhasvalue": {
                                const otherElementResult$ = ifOtherMatchesSubject.apply(this, [rules, ruleKey]);
                                const elementsIds = splitValidationList(rules["data-val-requireoneifotherhasvalue-elements"]);
                                /** @type {Observable<string[]>} */
                                const values$ = combineLatest(elementsIds.map(id => this.calculationContext.rootElement!.getValuesById$(id, this.vmId))).pipe(map(flattenArrays));
                                result$s.push(
                                    combineLatest(values$, otherElementResult$).pipe(
                                        map(([values, otherElementResult]) => {
                                            const [otherValue, isMatch] = otherElementResult;
                                            if (isMatch && isValueSet(values)) {
                                                const formattedMessage = formatIfOtherMessage(rules, otherValue, ruleKey);
                                                return [formattedMessage];
                                            }
                                            return [];
                                        }),
                                    ),
                                );
                                break;
                            }
                            case "data-val-requireone": {
                                const elementsIds = splitValidationList(rules["data-val-requireone-elements"]);
                                const values$ = combineLatest(elementsIds.map(id => this.calculationContext.rootElement!.getValuesById$(id, this.vmId))).pipe(map(flattenArrays));
                                result$s.push(values$.pipe(map(values => (isValueSet(values) ? [message] : []))));
                                break;
                            }
                            case "data-val-requireexactcount": {
                                simpleValidation(values => {
                                    if (values != null && values.length != parseInt(rules["data-val-requireexactcount-count"])) {
                                        return [message];
                                    }
                                });
                                break;
                            }
                            case "data-val-requiredvalueifotherhasvalue": {
                                ifOtherMatchesValidation.apply(this, [
                                    rules,
                                    ruleKey,
                                    (valueList: string[], otherValue: string[]) => {
                                        const matchType = rules["data-val-requiredvalueifotherhasvalue-valuematchtype"];
                                        const matchValue = rules["data-val-requiredvalueifotherhasvalue-value"];
                                        const matchList = splitValidationList(rules["data-val-requiredvalueifotherhasvalue-valuelist"]);
                                        if (!valueMatch(valueList, matchValue, matchList, matchType)) {
                                            const formattedMessage = formatIfOtherMessage(rules, otherValue, ruleKey);
                                            return [formattedMessage];
                                        }
                                    },
                                ]);
                                break;
                            }
                            case "data-val-requiredvalue": {
                                simpleValidation(values => {
                                    const matchValue = rules["data-val-requiredvalue-value"];
                                    const matchList = splitValidationList(rules["data-val-requiredvalue-valuelist"]);
                                    const matchType = rules["data-val-requiredvalue-valuematchtype"];
                                    if (!valueMatch(values, matchValue, matchList, matchType)) {
                                        return [message];
                                    }
                                });
                                break;
                            }
                            case "data-val-filesize": {
                                const fileSize$ = (this as unknown as { fileSize$: Observable<number | undefined> }).fileSize$;
                                if (fileSize$) {
                                    result$s.push(
                                        combineLatest(this.hasValue$(), fileSize$).pipe(
                                            map(([hasValue, fileSize]) => {
                                                if (hasValue) {
                                                    const maxSize = parseInt(rules["data-val-filesize-size"]);
                                                    if (fileSize != null && fileSize > maxSize) {
                                                        return [message];
                                                    }
                                                }
                                                return [];
                                            }),
                                        ),
                                    );
                                }
                                break;
                            }
                            case "data-val-extension": {
                                const fileName$ = (this as unknown as { fileName$: Observable<string | undefined> }).fileName$;
                                if (fileName$) {
                                    result$s.push(
                                        combineLatest(this.hasValue$(), fileName$).pipe(
                                            map(([hasValue, fileName]) => {
                                                if (hasValue) {
                                                    const fileExtensionsStr = rules["data-val-extension-extension"];
                                                    const fileExtensions = toUpperCaseList(fileExtensionsStr == null ? [] : fileExtensionsStr.split(",")); // TODO - convert both sides to splitValidationList
                                                    if (fileName != null) {
                                                        const upperFileName = toUpperCase(fileName);
                                                        if (!fileExtensions.some(i => upperFileName.endsWith("." + i))) {
                                                            return [message];
                                                        }
                                                    }
                                                }
                                                return [];
                                            }),
                                        ),
                                    );
                                }
                                break;
                            }
                            case "data-val-length": {
                                simpleValidation(values => {
                                    if (isValueSet(values)) {
                                        const minLength = parseInt(rules["data-val-length-min"]);
                                        const maxLength = parseInt(rules["data-val-length-max"]);
                                        const length = (values[0] + "").length;
                                        if (length < minLength || length > maxLength) {
                                            return [message];
                                        }
                                    }
                                });
                                break;
                            }
                            case "data-val-regex": {
                                simpleValidation(values => {
                                    if (isValueSet(values)) {
                                        const regex = rules["data-val-regex-pattern"];
                                        if (!new RegExp(regex).test(values[0] + "")) {
                                            return [message];
                                        }
                                    }
                                });
                                break;
                            }
                            //default:
                            //    throw "Error"
                        }
                    }
                    if (result$s.length == 0) {
                        return from([[] as string[]]);
                    }
                    const validateChild$ = this.parentElement == null ? from([true]) : this.parentElement._validateChild$(this as unknown as BaseElementDefBase);
                    return validateChild$.pipe(
                        switchMap(i => {
                            if (i) {
                                return combineLatest(result$s).pipe(map(results => flattenArrays(results)));
                            } else {
                                return from([[] as string[]]);
                            }
                        }),
                    );
                }),
            );
            // this._baseControlErrors$ = result.pipe(publishBehavior([]), refCount());
            /** @type { Observable<string[]> } */
            this._baseControlErrors$ = result.pipe(share({ resetOnRefCountZero: true, connector: () => new ReplaySubject(1) }));
        }
        return this._baseControlErrors$; //.pipe(finalize(() => console.log(`${oid} - Finished errors$ for ${this.definition.id}`)));
    }
}

export function isICustomConfirmElement(input: BaseElementDefBase): input is ICustomConfirmElement {
    //@ts-expect-error
    return input.hasCustomConfirmComponent || input.getCustomConfirmComponent;
}
export interface ICustomConfirmElement extends BaseElementDefBase {
    hasCustomConfirmComponent(adminPreview: boolean): boolean;
    getCustomConfirmComponent(adminPreview: boolean): Promise<{ component: any; data: any }>; // eslint-disable-line @typescript-eslint/no-explicit-any
}
export function isIBlockSubmitElement(input: BaseElementDefBase): input is IBlockSubmitElement {
    //@ts-expect-error
    return input.blockSubmit$;
}
export interface IBlockSubmitElement extends BaseElementDefBase {
    blockSubmit$(): Observable<boolean>;
}

export function isILockableElement(input: BaseElementDefBase): input is ILockableElement {
    //@ts-expect-error
    return input.getLockActions;
}
export type InnerLockAction = {
    unlock?: true;
    alreadyUnlocked?: true;
    redirectUrl?: string | (() => Promise<InnerLockAction>);
};
export type LockAction = InnerLockAction & {
    label: string;
};
export interface ILockableElement extends BaseElementDefBase {
    getLockActions(lock: string): LockAction[];
}

export function isIPostSubmitRedirectElement(input: BaseElementDefBase): input is IShouldSkipConfirmElement {
    //@ts-expect-error
    return input.getPostSubmitRedirect;
}
export interface IPostSubmitRedirectElement extends BaseElementDefBase {
    getPostSubmitRedirect(): Awaitable<string>;
}

export function isIShouldSkipCompletionElement(input: BaseElementDefBase): input is IShouldSkipCompletionElement {
    //@ts-expect-error
    return input.shouldSkipCompletion;
}
export interface IShouldSkipCompletionElement extends BaseElementDefBase {
    shouldSkipCompletion: () => boolean | null | undefined;
}

export function isIShouldSkipConfirmElement(input: BaseElementDefBase): input is IShouldSkipConfirmElement {
    //@ts-expect-error
    return input.shouldSkipConfirm || input.shouldSkipConfirm$;
}
export interface IShouldSkipConfirmElement extends BaseElementDefBase {
    shouldSkipConfirm?: () => Awaitable<boolean | null | undefined>;
    shouldSkipConfirm$?: () => Observable<boolean | null | undefined>;
}

export function isICustomSubmitTextElement(input: BaseElementDefBase): input is IHasCustomSubmitTextElement {
    //@ts-expect-error
    return input.submitText || input.submitText$;
}
export interface IHasCustomSubmitTextElement extends BaseElementDefBase {
    hasCustomSubmitText: () => Awaitable<boolean | null | undefined>;
    hasCustomSubmitText$: () => Observable<boolean | null | undefined>;
    submitText?: () => Awaitable<string | null | undefined>;
    submitText$?: () => Observable<string | null | undefined>;
}

export function isICustomReceiptElement(input: BaseElementDefBase): input is ICustomReceiptElement {
    //@ts-expect-error
    return input.isCustomReceiptContent;
}
export interface ICustomReceiptElement extends BaseElementDefBase {
    isCustomReceiptContent: true;
    visible(): Promise<boolean>;
    receiptContents(): Awaitable<string>;
}

export function isICustomViewElement(input: BaseElementDefBase): input is ICustomViewElement {
    //@ts-expect-error
    return input.isCustomViewType;
}
export interface ICustomViewElement extends BaseElementDefBase {
    isCustomViewType: true;
    visible(): Promise<boolean>;
    viewTypes(): string[];
}
